// src/components/Finanzas/ConfiguracionContable.jsx
// Admin: configuración singleton del módulo financiero.
// Permite ajustar diasGracia, fechaLimite, cuentasIVA, providerContable, retry.

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Form, InputGroup, Badge } from 'react-bootstrap';
import { FaCog, FaSave, FaInfoCircle, FaUsers, FaFileInvoice } from 'react-icons/fa';
import { Alertassw } from '../Alertas/Alertassw';
import SelectorProveedorTercero from './SelectorProveedorTercero';
import {
  obtenerConfiguracionContable,
  actualizarConfiguracionContable
} from '../../api/finanzas';
import { getAccountingProviders, getDataList } from '../../api/common';

/**
 * Si el valor empieza por '{' o '[' lo intenta parsear como JSON.
 * Devuelve { ok, parsed, error }.
 *  - ok: true → parsed es el objeto parseado o el string crudo (no-JSON).
 *  - ok: false → error es un mensaje legible.
 *  - Si el valor esta vacio, devuelve { ok: true, parsed: null } (campo limpio).
 */
const validarJsonSiAplica = (valor) => {
  const trimmed = (valor || '').trim();
  if (!trimmed) return { ok: true, parsed: null };
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return { ok: true, parsed: JSON.parse(trimmed) };
    } catch (e) {
      return { ok: false, error: 'JSON inválido. Revisa la sintaxis.' };
    }
  }
  return { ok: true, parsed: trimmed };
};

/** Convierte un valor Mixed del backend a su representacion editable (string). */
const tipoExternoToString = (valor) => {
  if (valor === null || valor === undefined) return '';
  if (typeof valor === 'object') return JSON.stringify(valor, null, 2);
  return String(valor);
};

const ConfiguracionContable2 = () => {
  const [config, setConfig] = useState(null);
  const [cuentas, setCuentas] = useState([]);
  const [providers, setProviders] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    diasGraciaRegistroGasto: 10,
    fechaLimiteCierreMensual: 10,
    cuentaIVA19: '',
    cuentaIVA5: '',
    cuentaIVA0: '',
    providerContable: '',
    maxReintentosSyncContable: 5,
    intervaloRetryMinutos: 15,
    emailsCriticos: '',
    // Terceros por tipo de comprobante: solo guardo el objeto Proveedor
    // completo para que el AsyncSelect se renderice con su label/value.
    // Al guardar mando solo el _id.
    terceroEntrega: null,
    terceroTraslado: null
  });

  // Estado separado para los inputs de "tipo externo": string crudo + error
  // de parseo JSON inline. Asi puedo bloquear "Guardar" cuando el JSON es
  // invalido sin destruir lo que el usuario escribio.
  const [tiposExternoRaw, setTiposExternoRaw] = useState({
    EGRESO_ENTREGA: '',
    EGRESO_TRASLADO: '',
    EGRESO_CIERRE: ''
  });
  const [erroresJson, setErroresJson] = useState({
    EGRESO_ENTREGA: null,
    EGRESO_TRASLADO: null,
    EGRESO_CIERRE: null
  });

  useEffect(() => {
    cargar();
  }, []);

  const cargar = async () => {
    setLoading(true);
    try {

      const [conf, cuents, provs] = await Promise.all([
        obtenerConfiguracionContable(),
        getDataList('cuentasContables', { conditions: { activo: true } }),
        getAccountingProviders().catch(() => ({ data: { data: [] } }))
      ]);

      if (conf?.status === 200) {
        const c = conf.data.data || {};
        setConfig(c);
        setForm({
          diasGraciaRegistroGasto: c.diasGraciaRegistroGasto ?? 10,
          fechaLimiteCierreMensual: c.fechaLimiteCierreMensual ?? 10,
          cuentaIVA19: c.cuentasIVA?.['19']?._id || c.cuentasIVA?.['19'] || '',
          cuentaIVA5: c.cuentasIVA?.['5']?._id || c.cuentasIVA?.['5'] || '',
          cuentaIVA0: c.cuentasIVA?.['0']?._id || c.cuentasIVA?.['0'] || '',
          providerContable: c.providerContable?._id || c.providerContable || '',
          maxReintentosSyncContable: c.maxReintentosSyncContable ?? 5,
          intervaloRetryMinutos: c.intervaloRetryMinutos ?? 15,
          emailsCriticos: (c.emailNotificacionesCriticas || []).join(', '),
          terceroEntrega: c.tercerosPorTipoComprobante?.EGRESO_ENTREGA || null,
          terceroTraslado: c.tercerosPorTipoComprobante?.EGRESO_TRASLADO || null
        });
        setTiposExternoRaw({
          EGRESO_ENTREGA: tipoExternoToString(c.tiposComprobanteExterno?.EGRESO_ENTREGA),
          EGRESO_TRASLADO: tipoExternoToString(c.tiposComprobanteExterno?.EGRESO_TRASLADO),
          EGRESO_CIERRE: tipoExternoToString(c.tiposComprobanteExterno?.EGRESO_CIERRE)
        });
        setErroresJson({
          EGRESO_ENTREGA: null,
          EGRESO_TRASLADO: null,
          EGRESO_CIERRE: null
        });
      }
      if (cuents?.status === 200) setCuentas(cuents.data.data || []);
      if (provs?.status === 200) setProviders(provs.data.data || []);
    } catch (err) {
      console.error(err);
      Alertassw('error', 'Error', 'No se pudo cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleTipoExternoChange = (tipo, valor) => {
    setTiposExternoRaw(prev => ({ ...prev, [tipo]: valor }));
    const r = validarJsonSiAplica(valor);
    setErroresJson(prev => ({ ...prev, [tipo]: r.ok ? null : r.error }));
  };

  const handleSeleccionTercero = (tipoForm, sel) => {
    // SelectorProveedorTercero llama onChange({ proveedor, proveedorSinCrear })
    const proveedor = sel?.proveedor || null;
    setForm(prev => ({ ...prev, [tipoForm]: proveedor }));
  };

  const tieneErroresJson = Object.values(erroresJson).some(Boolean);

  const handleGuardar = async () => {
    if (tieneErroresJson) {
      Alertassw('error', 'JSON inválido', 'Corrige los errores en "Tipo de comprobante en software contable" antes de guardar.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        diasGraciaRegistroGasto: Number(form.diasGraciaRegistroGasto),
        fechaLimiteCierreMensual: Number(form.fechaLimiteCierreMensual),
        cuentasIVA: {
          '19': form.cuentaIVA19 || null,
          '5': form.cuentaIVA5 || null,
          '0': form.cuentaIVA0 || null
        },
        providerContable: form.providerContable || null,
        maxReintentosSyncContable: Number(form.maxReintentosSyncContable),
        intervaloRetryMinutos: Number(form.intervaloRetryMinutos),
        emailNotificacionesCriticas: form.emailsCriticos
          .split(',')
          .map(s => s.trim())
          .filter(s => s.length > 0),
        tercerosPorTipoComprobante: {
          EGRESO_ENTREGA: form.terceroEntrega?._id || null,
          EGRESO_TRASLADO: form.terceroTraslado?._id || null
        },
        tiposComprobanteExterno: {
          EGRESO_ENTREGA: validarJsonSiAplica(tiposExternoRaw.EGRESO_ENTREGA).parsed,
          EGRESO_TRASLADO: validarJsonSiAplica(tiposExternoRaw.EGRESO_TRASLADO).parsed,
          EGRESO_CIERRE: validarJsonSiAplica(tiposExternoRaw.EGRESO_CIERRE).parsed
        }
      };
      const resp = await actualizarConfiguracionContable(payload);
      if (resp?.status === 200) {
        Alertassw('success', 'Configuración guardada');
        cargar();
      }
    } catch (err) {
      Alertassw('error', 'Error', err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  // Filtros para selects
  const cuentasIVA = cuentas.filter(c =>
    c.codigo?.startsWith('240810') ||
    (c.categoria || '').toLowerCase().includes('iva')
  );

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
      </Container>
    );
  }

  const tieneProvider = !!form.providerContable;

  return (
    <Container fluid className="py-4 w-100">
      <Row className="mb-4">
        <Col>
          <h2 className="text-primary fw-bold">
            <FaCog className="me-2" />
            Configuración Contable
          </h2>
          <p className="text-muted">Ajustes globales del módulo financiero</p>
        </Col>
      </Row>

      {!tieneProvider && (
        <Alert variant="warning">
          <FaInfoCircle className="me-1" />
          <strong>Sin proveedor configurado:</strong> los comprobantes se generarán en estado{' '}
          <Badge bg="warning">SYNC_NO_CONFIGURADO</Badge>. Configura un Provider activo para habilitar la sincronización.
        </Alert>
      )}

      <Row className="g-3">
        {/* Reglas de cierre */}
        <Col md={6}>
          <Card className="shadow-sm w-100">
            <Card.Header className="bg-light w-100">
              <h6 className="mb-0">Reglas de cierre</h6>
            </Card.Header>
            <Card.Body className='w-100'>
              <Form.Group className="mb-3">
                <Form.Label>Días de gracia para registrar gasto del mes anterior</Form.Label>
                <Form.Control type="number" min="0" max="30"
                  value={form.diasGraciaRegistroGasto}
                  onChange={(e) => setForm({ ...form, diasGraciaRegistroGasto: e.target.value })}
                />
                <Form.Text>Después de esta fecha del mes siguiente, se bloquea el registro de gastos del mes anterior.</Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Fecha límite mensual para cierre (día del mes 1-28)</Form.Label>
                <Form.Control type="number" min="1" max="28"
                  value={form.fechaLimiteCierreMensual}
                  onChange={(e) => setForm({ ...form, fechaLimiteCierreMensual: e.target.value })}
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        {/* Cuentas IVA */}
        <Col md={6}>
          <Card className="shadow-sm w-100">
            <Card.Header className="bg-light w-100">
              <h6 className="mb-0">Cuentas IVA descontable</h6>
            </Card.Header>
            <Card.Body className='w-100'>
              <Form.Group className="mb-3">
                <Form.Label>IVA 19%</Form.Label>
                <Form.Select value={form.cuentaIVA19}
                  onChange={(e) => setForm({ ...form, cuentaIVA19: e.target.value })}
                >
                  <option value="">— sin configurar —</option>
                  {cuentas.map((c) => (
                    <option key={c._id} value={c._id}>{c.codigo} - {c.nombre}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>IVA 5%</Form.Label>
                <Form.Select value={form.cuentaIVA5}
                  onChange={(e) => setForm({ ...form, cuentaIVA5: e.target.value })}
                >
                  <option value="">— sin configurar —</option>
                  {cuentas.map((c) => (
                    <option key={c._id} value={c._id}>{c.codigo} - {c.nombre}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>IVA 0% (exento)</Form.Label>
                <Form.Select value={form.cuentaIVA0}
                  onChange={(e) => setForm({ ...form, cuentaIVA0: e.target.value })}
                >
                  <option value="">— sin configurar —</option>
                  {cuentas.map((c) => (
                    <option key={c._id} value={c._id}>{c.codigo} - {c.nombre}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        {/* Sincronización */}
        <Col md={6}>
          <Card className="shadow-sm w-100">
            <Card.Header className="bg-light w-100">
              <h6 className="mb-0">Sincronización con software contable</h6>
            </Card.Header>
            <Card.Body className='w-100'>
              <Form.Group className="mb-3">
                <Form.Label>Proveedor contable activo</Form.Label>
                <Form.Select value={form.providerContable}
                  onChange={(e) => setForm({ ...form, providerContable: e.target.value })}
                >
                  <option value="">— sin configurar —</option>
                  {providers.map((p) => (
                    <option key={p._id} value={p._id}>{p.display_name || p.name}</option>
                  ))}
                </Form.Select>
                <Form.Text>Selecciona el Provider activo que recibirá los comprobantes.</Form.Text>
              </Form.Group>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Máx reintentos</Form.Label>
                    <Form.Control type="number" min="1" max="20"
                      value={form.maxReintentosSyncContable}
                      onChange={(e) => setForm({ ...form, maxReintentosSyncContable: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Intervalo retry (min)</Form.Label>
                    <Form.Control type="number" min="1" max="120"
                      value={form.intervaloRetryMinutos}
                      onChange={(e) => setForm({ ...form, intervaloRetryMinutos: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Notificaciones críticas */}
        <Col md={6}>
          <Card className="shadow-sm w-100">
            <Card.Header className="bg-light w-100">
              <h6 className="mb-0">Notificaciones críticas</h6>
            </Card.Header>
            <Card.Body className='w-100'>
              <Form.Group className="mb-3">
                <Form.Label>Emails que reciben alertas de error de sync (separados por coma)</Form.Label>
                <Form.Control as="textarea" rows={3}
                  value={form.emailsCriticos}
                  onChange={(e) => setForm({ ...form, emailsCriticos: e.target.value })}
                  placeholder="contabilidad@biolab.com, admin@biolab.com"
                />
                <Form.Text>
                  Estos emails reciben notificación cuando un comprobante falla la sincronización tras el máximo de reintentos.
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        {/* Terceros por tipo de comprobante */}
        <Col md={12}>
          <Card className="shadow-sm w-100">
            <Card.Header className="bg-light w-100 d-flex align-items-center">
              <FaUsers className="me-2" />
              <h6 className="mb-0">Terceros por tipo de comprobante</h6>
            </Card.Header>
            <Card.Body className='w-100'>
              <Alert variant="info" className="py-2 small mb-3">
                Cada movimiento de Entrega o Traslado generará un comprobante contable con el tercero configurado aquí.
                Para Cierre de gastos, el tercero se resuelve por gasto (no se configura aquí).
              </Alert>
              <Row>
                <Col md={6}>
                  <SelectorProveedorTercero
                    value={{ proveedor: form.terceroEntrega, proveedorSinCrear: null }}
                    onChange={(sel) => handleSeleccionTercero('terceroEntrega', sel)}
                  />
                  <Form.Text>Tercero para comprobantes de tipo Entrega de dinero.</Form.Text>
                </Col>
                <Col md={6}>
                  <SelectorProveedorTercero
                    value={{ proveedor: form.terceroTraslado, proveedorSinCrear: null }}
                    onChange={(sel) => handleSeleccionTercero('terceroTraslado', sel)}
                  />
                  <Form.Text>Tercero para comprobantes de tipo Traslado entre bolsillos.</Form.Text>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Tipo de comprobante en software contable */}
        <Col md={12}>
          <Card className="shadow-sm w-100">
            <Card.Header className="bg-light w-100 d-flex align-items-center">
              <FaFileInvoice className="me-2" />
              <h6 className="mb-0">Tipo de comprobante en software contable</h6>
            </Card.Header>
            <Card.Body className='w-100'>
              <Alert variant="info" className="py-2 small mb-3">
                Identificador del numerador en el software contable externo (id, código o JSON).
                Se copia al comprobante en el momento de su creación. Si pegas JSON, el sistema lo validará.
              </Alert>
              <Row className="g-3">
                {['EGRESO_ENTREGA', 'EGRESO_TRASLADO', 'EGRESO_CIERRE'].map((tipo) => {
                  const label = {
                    EGRESO_ENTREGA: 'Entrega de dinero',
                    EGRESO_TRASLADO: 'Traslado entre bolsillos',
                    EGRESO_CIERRE: 'Cierre de gastos'
                  }[tipo];
                  return (
                    <Col md={4} key={tipo}>
                      <Form.Group>
                        <Form.Label>{label}</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={tiposExternoRaw[tipo]}
                          onChange={(e) => handleTipoExternoChange(tipo, e.target.value)}
                          placeholder={'12345  o  {"id":12345,"prefix":"CE"}'}
                          isInvalid={!!erroresJson[tipo]}
                        />
                        {erroresJson[tipo] && (
                          <Form.Control.Feedback type="invalid">
                            {erroresJson[tipo]}
                          </Form.Control.Feedback>
                        )}
                        <Form.Text>
                          {tipo === 'EGRESO_CIERRE'
                            ? 'Tipo externo aplicado a los comprobantes de cierre mensual.'
                            : `Tipo externo aplicado a los comprobantes de ${label.toLowerCase()}.`}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  );
                })}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col xs="auto">
          <Button
            variant="primary"
            size="lg"
            onClick={handleGuardar}
            disabled={saving || tieneErroresJson}
          >
            <FaSave className="me-1" />
            {saving ? 'Guardando...' : 'Guardar configuración'}
          </Button>
          {tieneErroresJson && (
            <small className="ms-3 text-danger">
              Corrige los errores de JSON antes de guardar.
            </small>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default ConfiguracionContable2;
